import BaseApiService from './BaseApiService';
import {
    CognitiveAnalysisSessionDetail,
    CognitiveAnalyticsPipelineResponse,
    CognitiveAnalyticsProfileResponse,
    CognitiveAnalyticsTimeseriesResponse,
    CognitiveMode,
    ICharacterDto,
    TrainingCalendarDaySummary,
    TrainingCalendarSummaryResponse,
} from '../../shared/interfaces';
import Papa from 'papaparse';
import { getAuthToken } from './tokenStorage';

export type CharacterResourceId = 'ether_energy' | 'ether_structure';

export interface CharacterResourceSummaryDto {
    id: CharacterResourceId;
    icon_key: string;
    scope: 'account' | 'character';
    current: number;
    delta_7d: number;
    delta_30d: number;
    history_available: boolean;
}

export interface CharacterResourcesResponseDto {
    character_id: number;
    resources: CharacterResourceSummaryDto[];
    generated_at: string;
}

export interface CharacterResourcePointDto {
    timestamp: string;
    date: string;
    value: number;
}

export interface CharacterResourceSourceDto {
    source: string;
    delta: number;
    count: number;
}

export interface CharacterResourceEventDto {
    id: string;
    source: string;
    delta: number;
    balance_after: number;
    created_at: string;
    date_key: string;
}

export interface CharacterResourceHistoryResponseDto {
    character_id: number;
    resource_id: CharacterResourceId;
    icon_key: string;
    scope: 'account' | 'character';
    period: '7d' | '30d' | 'all';
    current: number;
    delta: number;
    from?: string;
    to: string;
    history_available: boolean;
    points: CharacterResourcePointDto[];
    sources: CharacterResourceSourceDto[];
    events: CharacterResourceEventDto[];
}

class CharactersApiService extends BaseApiService {
    constructor() {
        super('api');
    }
    public async getCharacters(): Promise<ICharacterDto[]> {
        return this.GET<ICharacterDto[]>('characters/fullset');
    }
    public async getCharacterCSV(characterId: number, level: string): Promise<any[]> {
        return this.GET<any[]>(`${characterId}/csv`, { params: { level } });
    }
    public async getCharacterGesture(characterId: number, level: string): Promise<any[]> {
        return this.GET<any[]>(`${characterId}/gesture`, { params: { level } });
    }
    public async getCharacterStat(characterId: number, level: string): Promise<any> {
        return this.GET<any>(`${characterId}/stat`, { params: { level } });
    }
    public async createCharacter(data: {
        accountName: string;
        characterName: string;
        race: string;
        aspect: string;
        gender: string;
        'custom:portrait': string;
    }): Promise<ICharacterDto> {
        return this.POST<ICharacterDto>('character/create', data, {
            headers: { 'Content-Type': 'application/json' },
        });
    }
    public async deleteCharacter(characterId: number): Promise<unknown> {
        return this.POST<unknown>('deleteCharacter', {
            characterId: String(characterId),
        }, {
            headers: { 'Content-Type': 'application/json' },
        });
    }
    public async postTask(files: File[]): Promise<any> {
        const formData = new FormData();
        files.forEach((file) => formData.append('files', file));
        return this.POST<any>('task', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }
    public async getCharacterFilesByDate(characterId: number, date: string): Promise<any> {
        return this.GET<any>(`characters/${characterId}/files`, { params: { date } });
    }
    public async getCharacterCSVData(characterId: number, date: string, type: 'raw' | 'processed' = 'processed'): Promise<any[]> {
        const response = await this.GET<string>(`characters/${characterId}/csv`, {
            params: { date, type },
            responseType: 'text',
        });
        const parsed = Papa.parse(response.replace(/^\uFEFF/, ''), {
            header: true,
            skipEmptyLines: true,
        });
        return parsed.data as any[];
    }
    public async getCharacterCSVByLevel(characterId: number, date: string, level?: string, typeEx?: string, createdAt?: string, fileType: 'raw' | 'processed' = 'processed', recordID?: string): Promise<any[]> {
        const token = getAuthToken();
        console.log('🔹 JWT token:', token);
        try {
            if (recordID) {
                console.log(`🔹 Fetching CSV for character ${characterId} with recordID ${recordID}`);
                const headers = { Authorization: `Bearer ${token}` };
                console.log('🔹 Request headers:', headers);
                const response = await this.GET<string>(`characters/${characterId}/csv/record/${recordID}`, {
                    params: { type: fileType },
                    responseType: 'text',
                    headers,
                });
                console.log('🔹 CSV raw string length:', response?.length ?? 0);
                console.log('🔹 CSV raw preview (first 200 chars):', response?.slice(0, 200));
                const csvClean = response.replace(/^\uFEFF/, '');
                const parsed = Papa.parse(csvClean, {
                    header: true,
                    skipEmptyLines: true,
                    delimiter: ';',
                });
                console.log('🔹 Parsed CSV rows count:', parsed.data.length);
                return parsed.data as any[];
            }
            console.log(`🔹 Fetching CSV by date: ${date}, level: ${level}`);
            const response = await this.GET<any>(`characters/${characterId}/csv`, {
                params: { date, level, typeEx, createdAt, type: fileType },
                headers: { Authorization: `Bearer ${token}` },
            });
            console.log('🔹 Old CSV fetch response:', response);
            return [];
        }
        catch (err) {
            console.error('❌ Error in getCharacterCSVByLevel:', err);
            return [];
        }
    }
    public async getCharacterMOTTextByRecord(
        characterId: number,
        recordID: string,
        fileType: 'raw' | 'processed' | 'gesture' | 'skeleton',
    ): Promise<string | null> {
        const token = getAuthToken();
        try {
            return await this.GET<string>(`characters/${characterId}/csv/record/${recordID}`, {
                params: { type: fileType },
                responseType: 'text',
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            });
        } catch (error: any) {
            if (error?.status === 404 || error?.response?.status === 404) return null;
            throw error;
        }
    }

    public async getCompletedQuests(characterId: number, date: string): Promise<any[]> {
        const token = getAuthToken();
        if (!token)
            throw new Error('JWT token is missing');
        try {
            const response = await this.GET<any>('quests/completed', {
                params: { characterId, date },
                headers: { Authorization: `Bearer ${token}` },
            });
            console.log('🔹 Completed quests received:', response);
            return response?.tasks || [];
        }
        catch (err) {
            console.error('Ошибка при загрузке завершённых квестов:', err);
            return [];
        }
    }

    public async getTrainingCalendarSummary(
        characterId: number,
        from: string,
        to: string,
    ): Promise<TrainingCalendarSummaryResponse> {
        const response = await this.GET<any>(`characters/${characterId}/training-calendar`, {
            params: { from, to },
        });

        const rawDays = Array.isArray(response)
            ? response
            : Array.isArray(response?.days)
                ? response.days
                : Array.isArray(response?.items)
                    ? response.items
                    : [];

        const days: TrainingCalendarDaySummary[] = rawDays
            .map((item: any) => {
                const date = String(item?.date ?? item?.Date ?? item?.day ?? item?.Day ?? '').slice(0, 10);
                const countsObject = item?.activityCounts
                    ?? item?.activity_counts
                    ?? item?.typeCounts
                    ?? item?.type_counts
                    ?? {};
                const readCount = (...values: unknown[]) => {
                    for (const value of values) {
                        const parsed = Number(value);
                        if (Number.isFinite(parsed))
                            return Math.max(0, parsed);
                    }
                    return 0;
                };

                const fitnessCount = readCount(
                    item?.fitnessCount,
                    item?.fitness_count,
                    countsObject?.fitness,
                    countsObject?.training,
                );
                const runningCount = readCount(
                    item?.runningCount,
                    item?.running_count,
                    countsObject?.running,
                    countsObject?.run,
                );
                const mmorpgCount = readCount(
                    item?.mmorpgCount,
                    item?.mmorpg_count,
                    countsObject?.mmorpg,
                    countsObject?.osteon,
                );
                const cognitiveCount = readCount(
                    item?.cognitiveCount,
                    item?.cognitive_count,
                    countsObject?.cognitive,
                );
                const vrCount = readCount(
                    item?.vrCount,
                    item?.vr_count,
                    countsObject?.vr,
                    countsObject?.betweenworld,
                );
                const wearCount = readCount(
                    item?.wearCount,
                    item?.wear_count,
                    item?.wearableCount,
                    item?.wearable_count,
                    countsObject?.wear,
                    countsObject?.wearable,
                    countsObject?.smartwatch,
                );
                const spiroCount = readCount(
                    item?.spiroCount,
                    item?.spiro_count,
                    item?.spirometerCount,
                    item?.spirometer_count,
                    countsObject?.spiro,
                    countsObject?.spirometer,
                );
                const fineMotorCount = readCount(
                    item?.fineMotorCount,
                    item?.fine_motor_count,
                    countsObject?.fineMotor,
                    countsObject?.fine_motor,
                );

                const canonicalTotal = fitnessCount
                    + runningCount
                    + mmorpgCount
                    + cognitiveCount
                    + vrCount
                    + wearCount
                    + spiroCount
                    + fineMotorCount;

                // Legacy categories are accepted during backend migration only.
                const workoutCount = readCount(item?.workoutCount, item?.workout_count, item?.trainings, item?.training_count);
                const questCount = readCount(item?.questCount, item?.quest_count, item?.quests);
                const minigameCount = readCount(item?.minigameCount, item?.minigame_count, item?.minigames);
                const legacyTotal = workoutCount + questCount + minigameCount;
                const explicitTotal = Number(item?.totalCount ?? item?.total_count ?? item?.count);
                const totalCount = Number.isFinite(explicitTotal)
                    ? Math.max(0, explicitTotal)
                    : canonicalTotal > 0
                        ? canonicalTotal
                        : legacyTotal;

                return {
                    date,
                    totalCount,
                    fitnessCount,
                    runningCount,
                    mmorpgCount,
                    cognitiveCount,
                    vrCount,
                    wearCount,
                    spiroCount,
                    fineMotorCount,
                    workoutCount,
                    questCount,
                    minigameCount,
                    totalDurationSeconds: Number(item?.totalDurationSeconds ?? item?.total_duration_seconds) || undefined,
                    totalCalories: Number(item?.totalCalories ?? item?.total_calories) || undefined,
                };
            })
            .filter((item: TrainingCalendarDaySummary) => /^\d{4}-\d{2}-\d{2}$/.test(item.date));

        return {
            characterId: Number(response?.characterId ?? response?.character_id ?? characterId),
            from: String(response?.from ?? from),
            to: String(response?.to ?? to),
            days,
        };
    }

    public async getCognitiveAnalyticsProfile(
        characterId: number,
        days = 90,
        gameName?: string,
    ): Promise<CognitiveAnalyticsProfileResponse> {
        return this.GET<CognitiveAnalyticsProfileResponse>('games/cognitive/analytics/profile', {
            params: { character_id: characterId, days, game_name: gameName || undefined },
        });
    }

    public async getCognitiveAnalyticsTimeseries(
        characterId: number,
        options: { days?: number; metric?: string; mode?: CognitiveMode; limit?: number } = {},
    ): Promise<CognitiveAnalyticsTimeseriesResponse> {
        return this.GET<CognitiveAnalyticsTimeseriesResponse>('games/cognitive/analytics/timeseries', {
            params: {
                character_id: characterId,
                days: options.days ?? 90,
                metric: options.metric || undefined,
                mode: options.mode || undefined,
                limit: options.limit ?? 5000,
            },
        });
    }

    public async getCognitiveAnalyticsPipeline(
        characterId: number,
    ): Promise<CognitiveAnalyticsPipelineResponse> {
        return this.GET<CognitiveAnalyticsPipelineResponse>('games/cognitive/analytics/pipeline', {
            params: { character_id: characterId },
        });
    }

    public async getCognitiveAnalyticsSessionDetail(
        characterId: number,
        analysisId: string,
    ): Promise<CognitiveAnalysisSessionDetail> {
        return this.GET<CognitiveAnalysisSessionDetail>(`games/cognitive/analytics/sessions/${encodeURIComponent(analysisId)}`, {
            params: { character_id: characterId },
        });
    }

    public async getMinigameSessions(characterId: number, date: string): Promise<any[]> {
        const token = getAuthToken();
        if (!token)
            throw new Error('JWT token is missing');
        try {
            const response = await this.GET<any>('minigames', {
                params: { characterId, date },
                headers: { Authorization: `Bearer ${token}` },
            });
            console.log('🔹 Minigame sessions received:', response);
            if (Array.isArray(response))
                return response;
            if (Array.isArray(response?.sessions))
                return response.sessions;
            if (Array.isArray(response?.data))
                return response.data;
            if (Array.isArray(response?.items))
                return response.items;
            return [];
        }
        catch (err) {
            console.error('Ошибка при загрузке миниигр:', err);
            return [];
        }
    }

    public async getCharacterResources(characterId: number): Promise<CharacterResourcesResponseDto> {
        return this.GET<CharacterResourcesResponseDto>(`characters/${characterId}/resources`);
    }

    public async getCharacterResourceHistory(
        characterId: number,
        resourceId: CharacterResourceId,
        period: '7d' | '30d' | 'all' = '30d',
    ): Promise<CharacterResourceHistoryResponseDto> {
        return this.GET<CharacterResourceHistoryResponseDto>(
            `characters/${characterId}/resources/${resourceId}/history`,
            { params: { period } },
        );
    }
}
export default new CharactersApiService();
